<template>
    <form-wizard
        ref="wizard"
        shape="square"
        v-bind="$attrs"
        v-on="$listeners"
        :class="{ 'hidden-navigation': navigationHidden }"
        :color="color"
        :error-color="errorColor"
        :title="localTitle"
        :subtitle="localSubtitle"
        @on-complete="formSubmitted"
        @on-change="onChange"
    >
        <tab-content
            v-for="(step, index) in flatSteps"
            ref="tabs"
            :key="index"
            :title="step.name"
            :icon="step.icon"
            class="mb-5 content"
            :before-change="() => runValidator(index)"
        >
            <component
                ref="tab"
                :is="step.component"
                :step="step"
                :form="value"
                v-bind="step.props"
                v-on="step.listeners"
                :navigation-hidden.sync="navigationHidden"
            />
        </tab-content>
    </form-wizard>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'

export default {
    name: 'vx-dynamic-form',
    components: {
        FormWizard,
        TabContent
    },
    props: {
        steps: {
            type: Array,
            required: true
        },
        value: {
            type: Object,
            default: () => {}
        },
        title: {
            type: String,
            default: null
        },
        subtitle: {
            type: String,
            default: null
        },
        color: {
            type: String,
            default: 'rgba(var(--vs-primary), 1)'
        },
        errorColor: {
            type: String,
            default: 'rgba(var(--vs-danger), 1)'
        }
    },
    watch: {
        stepCount: {
            handler: function (newV, oldV) {
                this.$emit('steps-updated', this.flatSteps)
            },
            immediate: true
        },
        flatSteps() {
            var i = this.activeStep + 1
            var tabs = this.$refs.tabs

            while (tabs[i]) {
                tabs[i].checked = false
                i++
            }
        }
    },
    data() {
        return {
            activeStep: 0,
            navigationHidden: false
        }
    },
    computed: {
        stepCount() {
            return (this.flatSteps && this.flatSteps.length) || 0
        },
        flatSteps() {
            var flatsteps = []

            const search = (currentStep) => {
                if (!currentStep) {
                    return
                }

                flatsteps.push(currentStep)

                if (Array.isArray(currentStep.next)) {
                    currentStep.next.forEach((step) => {
                        search(step)
                    })
                } else {
                    search(currentStep.next)
                }
            }

            this.steps.forEach((step) => search(step))
            return flatsteps
        },
        currentTab() {
            return this.getTab(this.activeStep)
        },
        currentStep() {
            return this.getStep(this.activeStep)
        },
        localTitle() {
            if (this.currentStep && this.currentStep.title) {
                return this.currentStep.title
            }
            return this.title
        },
        localSubtitle() {
            if (this.currentStep && this.currentStep.subtitle) {
                return this.currentStep.subtitle
            }
            return this.subtitle
        }
    },
    methods: {
        async runValidator(index) {
            // generic validation
            var tabComponent = this.getTab(index)
            var isValid = await tabComponent.validate()
            if (!isValid) {
                return false
            }

            // custom validation
            var sectionComponent = tabComponent.$children.first()
            if (sectionComponent && sectionComponent.validate) {
                isValid = await sectionComponent.validate()
            }
            return isValid
        },
        findAndSetAutofocus() {
            // find the first input with autofocus and focus it, this doesnt work as normal
            // as the entire form is rendered to the DOM at load so only the first autofocus
            // will work as expected. Page changes do not correctly trigger it.
            this.$nextTick(function () {
                const tabComponent = this.currentTab

                if (!tabComponent) {
                    return
                }

                var tabElement = tabComponent.$el
                const autofocusElem = tabElement.querySelector('input[autofocus]:first-of-type')

                if (autofocusElem) {
                    autofocusElem.focus()
                }
            })
        },
        onChange(prevIndex, nextIndex) {
            this.activeStep = nextIndex
            this.$emit('active-step-changed', prevIndex, nextIndex)
            this.findAndSetAutofocus()
        },
        getTab(index) {
            if (this.$refs.tabs) {
                return this.$refs.tabs[index]
            }
            return null
        },
        getStep(index) {
            return this.flatSteps[this.activeStep] || null
        },
        formSubmitted() {
            var immutableResult = this.deepCopy(this.value)
            // this.reset()
            this.$emit('form-completed', immutableResult)
        },
        reset() {
            this.$refs.wizard.reset()
            this.resetValidation()
        },
        activateAllSteps() {
            this.$refs.wizard.activateAll()
        }
    },
    mounted() {
        this.findAndSetAutofocus()
    }
}
</script>

<style scoped>
.content {
    min-height: 150px;
}
.hidden-navigation >>> .wizard-card-footer {
    pointer-events: none;
    opacity: 0;
}
</style>
